import { forwardRef, useImperativeHandle } from "react";
import {
  GetProgramFeeResponse,
  IdNameList,
} from "../../../../../../../../../api/services/common/models";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../../components";
import { CurrencyFormat } from "../../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../../../../../../../components/textFieldCurrency/TextFieldCurrency";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  MAX_LENGTH_100,
  MAX_LENGTH_1000,
} from "../../../../../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../../../../../helpers/constants/siteCoreKeys";
import {
  isEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../../../../helpers/utils";
import {
  ProgramDetail,
  ProgramFeeTabFormKey,
} from "../../../../../../../modelsAndSchemas/Models";

import { BillingTypeFieldInvoiceWrapper } from "./BillingTypeFieldInvoice.style";
import { useBillingTypeFieldInvoice } from "./useBillingTypeFieldInvoice";

export interface BillingTypeFieldInvoicePropsForProgram {
  pageId: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  defaultUserCurrency: string;
  currenciesList: IdNameList[];
  forceUpdate: () => void;
  programTemplateId: string;
  setShowWarningOnBillingChange: (value: boolean) => void;
  programData?: ProgramDetail;
  programFeeDetailsData?: GetProgramFeeResponse;
}

export const BillingTypeFieldInvoice = forwardRef(
  (props: BillingTypeFieldInvoicePropsForProgram, ref) => {
    const {
      currencyFormat,
      currencyId,
      defaultUserCurrency,
      currenciesList,
      forceUpdate,
      programTemplateId,
      pageId,
      gridSize,
      isTablet,
      isMobile,
      commonARIALabel,
      control,
      setValue,
      commonLabels,
      getDropdownValues,
      getLabel,
      fetchValidationMessage,
      commonValidationMessages,
      handleConfirmationStatusTrue,
      handleConfirmationStatusFalse,
      billingTypeWatch,
      billingTypeKey,
      feeKey,
      feeWithFormatKey,
      billingTriggerIdKey,
      minimumTransferFeeKey,
      minimumTransferFeeWithFormatKey,
      earlyPayDaysKey,
      earlyPayPercentageKey,
      proBonoCategoryKey,
      specialBillingInstructionsKey,
      earlyPayDays,
      earlyPayPercentage,
      minimumTransferFee,
      billingTrigger,
      currency,
      programFee,
      billingTriggerList,
      billingTypeList,
      billingTypeSiteCore,
      proBonoCategory,
      proBonoCategoryList,
      specialBillingInstructions,
      earlyPayPercentageToolTipMessage,
      shouldIgnoreZeroValidationForFee,
      isProgramStatusActiveAlready,
      handleBillingTypeChange,
      shouldDisableProBonoField,
      shouldDisableProgramFee,
      shouldDisableBillingType,
      shouldDisableOtherFields,
    } = useBillingTypeFieldInvoice({ ...props });

    useImperativeHandle(ref, () => ({
      handleConfirmationStatusTrue,
      handleConfirmationStatusFalse,
    }));

    const billingTypeIdFieldInvoice = (
      <MuiGrid size={gridSize}>
        <FormController control={control} name={billingTypeKey}>
          <MuiSelect
            id={`${pageId}-${billingTypeKey}`}
            label={getLabel(billingTypeSiteCore)}
            options={getDropdownValues(billingTypeList)}
            defaultValue={""}
            disabled={shouldDisableBillingType}
            visibleDefaultSelectOption={true}
            isMandatory
            placeholder={commonLabels.selectPlaceHolder}
            onChange={handleBillingTypeChange}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
    );

    const proBonoFieldsInvoice = (
      <>
        <MuiBox className="dashed-line" />
        <MuiGrid container spacing={2} className="padding-bottom">
          <MuiGrid size={gridSize}>
            <FormController control={control} name={proBonoCategoryKey}>
              <MuiSelect
                id={`${pageId}-${proBonoCategoryKey}`}
                label={getLabel(proBonoCategory)}
                options={getDropdownValues(proBonoCategoryList)}
                visibleDefaultSelectOption={true}
                disabled={shouldDisableProBonoField}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </>
    );

    const specialBillingInstructionsFieldsInvoice = (
      <MuiGrid
        container
        spacing={2}
        className="padding-bottom billing-instructions"
      >
        <MuiGrid size={{ md: 8, sm: 12 }}>
          <FormController
            control={control}
            name={specialBillingInstructionsKey}
          >
            <MuiTextField
              id={`${pageId}-${specialBillingInstructionsKey}`}
              label={getLabel(specialBillingInstructions)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_1000}
              multiline
              rows={4}
              data-testid={`${pageId}-${specialBillingInstructionsKey}`}
              requiredARIALabel={commonARIALabel.required}
              isTextarea
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    );

    const commonFieldsInvoice = (
      <>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={`${ProgramFeeTabFormKey.currencyId}`}
          >
            <MuiSelect
              data-testId={`${pageId}-${ProgramFeeTabFormKey.currencyId}`}
              label={getLabel(currency)}
              options={setApiDataToDropDownOption(currenciesList)}
              defaultValue={defaultUserCurrency}
              onChange={forceUpdate}
              disabled={shouldDisableOtherFields}
              visibleDefaultSelectOption={false}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController control={control} name={feeWithFormatKey}>
            <TextFieldCurrency
              isMandatory
              id={`${pageId}-${ProgramFeeTabFormKey.feeWithFormat}`}
              formId={feeWithFormatKey}
              locale={currencyFormat?.locale}
              isCurrencyChanged={currencyId}
              ignoreZeroValidation={shouldIgnoreZeroValidationForFee}
              disabled={shouldDisableProgramFee}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              formatErrorMessage={fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              )}
              name={feeWithFormatKey}
              label={getLabel(programFee)}
              placeholder={commonLabels.inputPlaceHolder}
              getFinalValues={(value, valueNonFormatted) => {
                setValue(feeKey, valueNonFormatted, {
                  shouldTouch: true,
                });
                setValue(feeWithFormatKey, value, {
                  shouldTouch: true,
                });
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={minimumTransferFeeWithFormatKey}
          >
            <TextFieldCurrency
              id={`${pageId}-${minimumTransferFeeWithFormatKey}`}
              formId={minimumTransferFeeWithFormatKey}
              locale={currencyFormat?.locale}
              isCurrencyChanged={currencyId}
              disabled={shouldDisableOtherFields}
              ignoreZeroValidation={shouldIgnoreZeroValidationForFee}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              formatErrorMessage={fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              )}
              name={minimumTransferFeeWithFormatKey}
              label={getLabel(minimumTransferFee)}
              placeholder={commonLabels.inputPlaceHolder}
              getFinalValues={(value, valueNonFormatted) => {
                setValue(minimumTransferFeeKey, valueNonFormatted, {
                  shouldTouch: true,
                });
                setValue(minimumTransferFeeWithFormatKey, value, {
                  shouldTouch: true,
                });
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController control={control} name={earlyPayDaysKey}>
            <MuiTextField
              id={`${pageId}-${earlyPayDaysKey}`}
              label={getLabel(earlyPayDays)}
              disabled={shouldDisableOtherFields}
              placeholder={commonLabels.inputPlaceHolder}
              type="number"
              maxLength={MAX_LENGTH_100}
              data-testId={`${pageId}-${earlyPayDaysKey}`}
              inputNumberWithoutDecimal
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <FormController control={control} name={earlyPayPercentageKey}>
            <MuiTextField
              id={`${pageId}-${earlyPayPercentageKey}`}
              label={getLabel(earlyPayPercentage)}
              disabled={shouldDisableOtherFields}
              placeholder={commonLabels.inputPlaceHolder}
              type="number"
              maxLength={MAX_LENGTH_100}
              infoMessage={getLabel(earlyPayPercentageToolTipMessage)}
              shouldDisableInfoIcon={shouldDisableOtherFields}
              data-testId={`${pageId}-${earlyPayPercentageKey}`}
              inputNumberWithoutDecimal
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </>
    );

    if (isEmpty(programTemplateId)) return null;

    return (
      <BillingTypeFieldInvoiceWrapper>
        <MuiGrid container spacing={2}>
          {billingTypeIdFieldInvoice}
          {billingTypeWatch ===
            BILLING_DETAILS_SITE_CORE_VALUE.billingTypeActivity && (
            <>
              <MuiGrid size={gridSize}>
                <FormController control={control} name={billingTriggerIdKey}>
                  <MuiSelect
                    data-testId={`${pageId}-${ProgramFeeTabFormKey.billingTriggerId}`}
                    label={getLabel(billingTrigger)}
                    options={getDropdownValues(billingTriggerList)}
                    disabled={isProgramStatusActiveAlready}
                    defaultValue={""}
                    visibleDefaultSelectOption={true}
                    isMandatory
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
              {!isTablet && !isMobile && <MuiGrid size={gridSize} />}
            </>
          )}
          {commonFieldsInvoice}
        </MuiGrid>

        {proBonoFieldsInvoice}
        {specialBillingInstructionsFieldsInvoice}
      </BillingTypeFieldInvoiceWrapper>
    );
  },
);
